import React, { useEffect, useState } from 'react';
import { capitalizeName, getErrorMessage } from '@utils/commons';
import { ResidentDetails, ContactInfoType } from '@typings/ResidentTypes';
import { useErrors } from '@contexts/ErrorsContext';
import WarningText from '@components/WarningText';

export interface NextOfKinDetails {
  fullName: string;
  relationship: string;
  phoneNumber: string;
}
interface ContactFormProps {
  residentDetails: ResidentDetails | undefined;
  nextOfKinDetails: NextOfKinDetails;
  setNextOfKinDetails: (info: NextOfKinDetails) => void;
  contactInfo: ContactInfoType;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfoType>>;
}

const ContactForm: React.FC<ContactFormProps> = ({
  residentDetails,
  nextOfKinDetails,
  setNextOfKinDetails,
  contactInfo,
  setContactInfo,
}) => {
  const [residentFullName, setResidentFullName] = useState('');
  const { errors } = useErrors();
  const [emailErrorMessage, setEmailErrorMessage] = useState<
    string | undefined
  >('');
  const [assignInfo, setAssignInfo] = useState<{
    hasNextOfKin: boolean;
    isDisabled: boolean;
    isChecked: boolean;
  }>({
    hasNextOfKin: false,
    isDisabled: false,
    isChecked: false,
  });

  useEffect(() => {
    setEmailErrorMessage(getErrorMessage(errors, 'lpaContacEmail'));
  }, [errors]);

  useEffect(() => {
    if (residentDetails) {
      const {
        profile: {
          full_name: fullName,
          nfk_name: nfkFullName,
          nfk_phone: nfkPhoneNumber,
          nfk_relationship: nfkRelationship,
        },
      } = residentDetails;

      setResidentFullName(fullName);
      setNextOfKinDetails({
        fullName: nfkFullName || '',
        relationship: nfkRelationship || '',
        phoneNumber: nfkPhoneNumber || '',
      });
    }
  }, [residentDetails, setNextOfKinDetails]);

  const handleInputChange = (field: 'fullName' | 'email', value: string) => {
    setContactInfo(prevState => ({ ...prevState, [field]: value }));
  };

  useEffect(() => {
    setAssignInfo({
      ...assignInfo,
      hasNextOfKin:
        !!nextOfKinDetails.fullName && !!nextOfKinDetails.relationship,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextOfKinDetails]);

  const handleCheckboxChange = async (e: any) => {
    const { checked } = e.target;

    if (checked) {
      if (assignInfo.hasNextOfKin) {
        setContactInfo({
          ...contactInfo,
          fullName: nextOfKinDetails.fullName || contactInfo.fullName,
          relationship:
            nextOfKinDetails.relationship || contactInfo.relationship,
        });
        setAssignInfo({ ...assignInfo, isChecked: checked });
      } else {
        setAssignInfo(prev => ({ ...prev, isChecked: true }));
        setTimeout(() => {
          setAssignInfo(prev => ({
            ...prev,
            isChecked: false,
            isDisabled: true,
          }));
        }, 500);
      }
    } else if (!checked) {
      setAssignInfo(prev => ({
        ...prev,
        isChecked: false,
      }));
      if (assignInfo.hasNextOfKin) {
        setContactInfo({
          ...contactInfo,
          fullName: '',
          relationship: '',
        });
      }
    }
  };

  return (
    <div>
      <div className="govuk-form-group">
        <fieldset
          className="govuk-fieldset"
          aria-describedby="asigned-nok-hint"
        >
          <div className="govuk-checkboxes" data-module="govuk-checkboxes">
            <div className="govuk-checkboxes__item">
              <input
                className="govuk-checkboxes__input"
                id="asigned-nok"
                name="asigned-nok"
                type="checkbox"
                checked={assignInfo.isChecked}
                disabled={assignInfo.isDisabled}
                onChange={handleCheckboxChange}
              />
              <label
                className="govuk-label govuk-checkboxes__label"
                htmlFor="asigned-nok"
              >
                If this person is the assigned Next of Kin for{' '}
                {residentFullName ? capitalizeName(residentFullName) : '...'}{' '}
                check the box.
              </label>
            </div>
          </div>
        </fieldset>
        {assignInfo.isDisabled && (
          <WarningText content="A Next of Kin has not been added to this care plan" />
        )}
      </div>

      <div className="govuk-form-group">
        <label className="govuk-label" htmlFor="lpaContacFullName">
          Full Name
        </label>
        <div className="govuk-hint" id="full-name-hint" />
        <input
          className="govuk-input govuk-input--width-20"
          id="lpaContactFullName"
          name="fullName"
          type="text"
          disabled={assignInfo.isChecked}
          autoComplete="fullName"
          value={contactInfo.fullName}
          onChange={e => handleInputChange('fullName', e.target.value)}
        />
      </div>
      <div
        className={`govuk-form-group ${emailErrorMessage ? 'govuk-form-group--error' : ''}`}
      >
        <label className="govuk-label" htmlFor="lpaContacEmail">
          Email Address
        </label>
        <input
          className={`govuk-input govuk-input--width-20 ${emailErrorMessage ? 'govuk-input--error' : ''}`}
          id="lpaContacEmail"
          name="email"
          type="email"
          spellCheck={false}
          aria-describedby={emailErrorMessage ? 'email-input-error' : undefined}
          autoComplete="email"
          value={contactInfo.email}
          onChange={e => handleInputChange('email', e.target.value)}
        />
        {emailErrorMessage && (
          <p id="full-name-input-error" className="govuk-error-message">
            <span className="govuk-visually-hidden">Error:</span>
            {emailErrorMessage}
          </p>
        )}
      </div>
      <div className="govuk-form-group">
        <label className="govuk-label" htmlFor="phone-number">
          Phone Number
        </label>
        <input
          className="govuk-input govuk-input--width-20"
          id="phone-number"
          name="phoneNumber"
          type="tel"
          autoComplete="tel"
          aria-describedby="having-conversations-phone-number"
          maxLength={11}
          value={contactInfo.phoneNumber}
          onChange={e => {
            const { value } = e.target;
            // Check if value only contains numbers and has a maximum of 11 digits
            if (/^[0-9]*$/.test(value) && value.length <= 11) {
              setContactInfo({
                ...contactInfo,
                phoneNumber: value,
              });
            }
          }}
        />
      </div>
      <div className="govuk-form-group">
        <label className="govuk-label" htmlFor="relationship-input">
          Relationship
        </label>
        <div className="govuk-hint" id="relationship-hint" />
        <input
          className="govuk-input govuk-input--width-20"
          id="relationship-input"
          name="relationship"
          type="text"
          disabled={assignInfo.isChecked}
          autoComplete="relationship"
          value={contactInfo.relationship}
          onChange={e =>
            setContactInfo({ ...contactInfo, relationship: e.target.value })
          }
        />
      </div>
    </div>
  );
};

export default ContactForm;
